import React, { useEffect, useMemo, useState } from "react"
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Row,
  Col,
} from "reactstrap"
import { useTranslation } from "react-i18next"
import { useSelector, useDispatch } from "react-redux"
import { useFormik } from "formik"
import * as Yup from "yup"

import CustomLabel from "components/Common/CustomLabel"
import {
  addCatering,
  addRoasterCatering,
  updateCatering,
  updateRoasterCatering,
} from "store/actions/cateringAction"
import { showAlert } from "store/reducers/alertReducer"
import { getTrialPeriodSchedule } from "store/actions/trialPeriodAction"
import { formatDateForAPI } from "components/Common/Utils"
import CustomSingleSelect from "components/Shared/CustomSingleSelect"

const status = {
  received: "Received",
  not_received: "Not Received",
}

const ErrorMessage = ({ name, errors, touched }) => {
  if (!errors[name] || !touched[name]) return null

  return <div className="text-danger fw-semibold mt-1">{errors[name]}</div>
}

const AddEditCatering = ({
  isOpen,
  toggle,
  triger,
  catering,
  mode = "add",
  showTrialOperation,
}) => {
  console.log({ catering })
  const [hovered, setHovered] = useState(null)
  const [trialPeriodSchedules, setTrialPeriodSchedules] = useState([])

  const {
    t,
    i18n: { language },
  } = useTranslation()
  const { work_shifts, stations, mealTypes } = useSelector(
    state => state.Common
  )
  const dispatch = useDispatch()

  const initialValues = useMemo(() => {
    if (showTrialOperation) {
      return {
        shift: catering?.shift || "",
        station: catering?.station || "",
        meal_type: catering?.meal_type || "",
        status: catering?.status || "",
        trial_run_schedule_uuid:
          trialPeriodSchedules.find(
            item => item.title === catering?.trial_run_schedule
          )?.uuid || "",
        notes: catering?.notes || "",
        meal_date: catering?.meal_date || formatDateForAPI(new Date()),
        is_trial_run: true,
      }
    } else {
      return {
        shift: catering?.shift || "",
        station: catering?.station || "",
        meal_type: catering?.meal_type || "",
        status: catering?.status || "",
        meal_date: catering?.meal_date || formatDateForAPI(new Date()),
        notes: catering?.notes || "",
      }
    }
  }, [showTrialOperation, trialPeriodSchedules, catering])

  const validationSchema = useMemo(() => {
    if (showTrialOperation) {
      return Yup.object().shape({
        shift: Yup.string().required(t("Field is required")),
        station: Yup.string().required(t("Field is required")),
        meal_type: Yup.string().required(t("Field is required")),
        status: Yup.string().required(t("Field is required")),
        trial_run_schedule_uuid: Yup.string().required(t("Field is required")),
        notes: Yup.string(),
      })
    } else {
      return Yup.object().shape({
        shift: Yup.string().required(t("Field is required")),
        station: Yup.string().required(t("Field is required")),
        meal_type: Yup.string().required(t("Field is required")),
        status: Yup.string().required(t("Field is required")),
        notes: Yup.string(),
      })
    }
  }, [showTrialOperation])

  const buttonStyle = (selected, text, isHovered) => {
    return {
      flex: "1 1 100px",
      margin: "4px",
      padding: "10px 15px",
      border: `1px solid  ${
        selected === text || isHovered
          ? "#00778a"
          : text === "received"
          ? "green"
          : text === "not_received"
          ? "red"
          : "#99c1cd"
      }`,
      background: selected === text || isHovered ? "#00778a" : "white",
      color:
        selected === text || isHovered
          ? "white"
          : text === "not_received"
          ? "red"
          : text === "received"
          ? "green"
          : "#5d98a0",
      fontSize: "14px",
      cursor: "pointer",
      fontWeight: selected === text ? "bold" : "normal",
      transition: "all 0.3s ease",
      borderRadius: "5px",
      minWidth: "120px",
    }
  }

  const onSubmit = values => {
    dispatch(
      mode === "add" && showTrialOperation
        ? addCatering({
            ...values,
            status: values.status === "received" ? 1 : 0,
          })
        : mode !== "add" && showTrialOperation
        ? updateCatering({
            payload: {
              ...values,
              status: values.status === "received" ? 1 : 0,
            },
            uuid: catering.uuid,
          })
        : mode === "add" && !showTrialOperation
        ? addRoasterCatering({
            ...values,
            status: values.status === "received" ? 1 : 0,
          })
        : updateRoasterCatering({
            data: {
              ...values,
              status: values.status === "received" ? 1 : 0,
            },
            id: catering.uuid,
          })
    )
      .then(action => {
        const { statusCode, success, message } = action?.payload
        if (statusCode === 200 && success) {
          dispatch(showAlert({ text: message, type: "success" }))
          triger()
          toggle()
        } else {
          dispatch(showAlert({ text: message, type: "error" }))
        }
      })
      .catch(() => {})
  }

  const {
    values,
    errors,
    touched,
    setFieldValue,
    handleSubmit,
    handleBlur,
    setFieldTouched,
    isValid,
  } = useFormik({
    initialValues,
    validationSchema,
    onSubmit,
    enableReinitialize: true,
  })

  useEffect(() => {
    if (values.shift && values.station) {
      getTrailSchedule({ shift: values.shift, station: values.station })
    }
  }, [values.shift, values.station])

  const getTrailSchedule = data => {
    let obj = {
      ...data,
      date: formatDateForAPI(new Date()),
    }
    dispatch(getTrialPeriodSchedule(obj))
      .then(action => {
        const { statusCode, success, message, data } = action?.payload
        if (statusCode === 200 && success) {
          setTrialPeriodSchedules(data)
        } else {
          dispatch(showAlert({ text: message, type: "error" }))
        }
      })
      .catch(() => {})
  }

  const formattedTrialPeriodSchedules = useMemo(() => {
    if (!trialPeriodSchedules) return []
    const updatedTrialPeriodSchedules = trialPeriodSchedules.map(item => ({
      label: item.title,
      value: item.uuid,
    }))
    if (mode === "add")
      setFieldValue(
        "trial_run_schedule_uuid",
        updatedTrialPeriodSchedules[0]?.value
      )
    return updatedTrialPeriodSchedules
  }, [trialPeriodSchedules])

  useEffect(() => {
    if (
      mode === "edit" &&
      formattedTrialPeriodSchedules.length > 0 &&
      values.trial_run_schedule_uuid === null
    ) {
      setFieldValue(
        "trial_run_schedule_uuid",
        formattedTrialPeriodSchedules.find(
          item => item.label === catering.trial_run_schedule
        )?.value
      )
    }
  }, [mode, formattedTrialPeriodSchedules])

  return (
    <Modal size="lg" isOpen={isOpen} toggle={toggle} centered>
      <ModalHeader toggle={toggle}>
        {t(`${mode === "add" ? "Add" : "Edit"} Catering`)}
      </ModalHeader>
      <ModalBody>
        <Row>
          <Col xs={12} className="mb-2">
            <CustomLabel label={t("Shift")} isRequired />
            <div className="d-flex flex-wrap">
              {Object.keys(work_shifts).map(option => (
                <button
                  key={option}
                  name="shift"
                  onMouseEnter={() => setHovered(option)}
                  onMouseLeave={() => setHovered(null)}
                  onBlur={handleBlur}
                  style={buttonStyle(values.shift, option, hovered === option)}
                  onClick={() => {
                    setFieldValue("shift", option)
                  }}
                >
                  {work_shifts
                    ? work_shifts[option][
                        language === "en" ? "name" : "name_ar"
                      ]
                    : "-"}
                </button>
              ))}
            </div>
            <ErrorMessage errors={errors} touched={touched} name="shift" />
          </Col>

          <Col xs={12} className="mb-2">
            <CustomLabel label={t("Select the station")} isRequired />
            <div className="d-flex flex-wrap">
              {Object.keys(stations).map(option => (
                <button
                  key={option}
                  name="station"
                  onBlur={handleBlur}
                  onMouseEnter={() => setHovered(option)}
                  onMouseLeave={() => setHovered(null)}
                  style={buttonStyle(
                    values.station,
                    option,
                    hovered === option
                  )}
                  onClick={() => {
                    setFieldValue("station", option)
                  }}
                >
                  {stations
                    ? stations[option][language === "en" ? "name" : "name_ar"]
                    : "-"}
                </button>
              ))}
            </div>
            <ErrorMessage errors={errors} touched={touched} name="station" />
          </Col>
          <Col xs={12} className="mb-2">
            <CustomLabel label={t("Select meal type")} isRequired />
            <div className="d-flex flex-wrap">
              {Object.keys(mealTypes).map(option => (
                <button
                  key={option}
                  name="meal_type"
                  onBlur={handleBlur}
                  onMouseEnter={() => setHovered(option)}
                  onMouseLeave={() => setHovered(null)}
                  style={buttonStyle(
                    values.meal_type,
                    option,
                    hovered === option
                  )}
                  onClick={() => {
                    setFieldValue("meal_type", option)
                  }}
                >
                  {mealTypes
                    ? mealTypes[option][language === "en" ? "name" : "name_ar"]
                    : "-"}
                </button>
              ))}
            </div>
            <ErrorMessage errors={errors} touched={touched} name="meal_type" />
          </Col>
          <Col xs="12" className="mb-3">
            <CustomLabel label={t("Status")} isRequired />
            <div className="d-flex flex-wrap">
              {Object.keys(status).map(option => (
                <button
                  key={option}
                  name="status"
                  onBlur={handleBlur}
                  onMouseEnter={() => setHovered(option)}
                  onMouseLeave={() => setHovered(null)}
                  style={buttonStyle(values.status, option, hovered === option)}
                  onClick={() => {
                    setFieldValue("status", option)
                  }}
                >
                  {t(status[option])}
                </button>
              ))}
            </div>
            <ErrorMessage errors={errors} touched={touched} name="status" />
          </Col>
          {showTrialOperation && (
            <Col md="6" xs="12" className="">
              <CustomLabel label={t("Trial Operation")} isRequired />
              <CustomSingleSelect
                value={formattedTrialPeriodSchedules.find(
                  i => i.value === values.trial_run_schedule_uuid
                )}
                options={formattedTrialPeriodSchedules}
                onChange={e => {
                  setFieldValue("trial_run_schedule_uuid", e?.value)
                  setFieldTouched("trial_run_schedule_uuid", true) // <-- optional for better UX
                }}
                placeholder={t("Select trial operation")}
                name="trial_run_schedule_uuid"
                onBlur={() => setFieldTouched("trial_run_schedule_uuid", true)} // <-- Add this
              />
              <ErrorMessage
                errors={errors}
                touched={touched}
                name="trial_run_schedule_uuid"
              />
            </Col>
          )}

          {/* <Col md={6} xs={12} className="d-flex flex-row gap-3">
            <CustomLabel label={t("Is Trial Run")} isRequired />

            {[
              { id: 1, label: "Yes" },
              { id: 0, label: "No" },
            ].map(option => (
              <label
                key={option.id}
                className="radio_label d-flex align-items-center gap-2 flex-row"
              >
                <input
                  type="radio"
                  name={option.label} // Ensures only one selection per question
                  value={option.id}
                  checked={values.is_trial_run === option.id}
                  onClick={() => setFieldValue("is_trial_run", option.id)}
                />

                {t(option.label)}
              </label>
            ))}
            <ErrorMessage
              errors={errors}
              touched={touched}
              name="is_trial_run"
            />
          </Col> */}
          <Col xs={12}>
            <div className="form_field">
              <CustomLabel htmlFor="note" label={t("Note")} />
              <textarea
                name="message"
                rows="3"
                cols="30"
                maxLength={250}
                className="p-2"
                value={values.notes}
                onChange={e => setFieldValue("notes", e.target.value)}
                placeholder={t("Enter Note")}
              ></textarea>
            </div>
          </Col>
        </Row>
      </ModalBody>
      <ModalFooter>
        <div className="d-flex flex-wrap gap-2">
          <button
            type="button"
            className="main_btn disabled-button fit_content px-4"
            onClick={toggle}
          >
            {t("Cancel")}
          </button>
          <button
            type="button"
            className="main_btn fit_content px-4"
            onClick={handleSubmit}
            disabled={!isValid}
          >
            {t("Save")}
          </button>
        </div>
      </ModalFooter>
    </Modal>
  )
}

export default AddEditCatering
